import type {
  DataDrivenPropertyValueSpecification,
  ExpressionSpecification,
  FillLayerSpecification,
  FilterSpecification,
  LineLayerSpecification,
  Map as MapLibreMap,
  VectorSourceSpecification,
} from 'maplibre-gl';

import { MAPLIBRE_WATER_LAYER_ID } from '../maplibre-basemap';
import { LocationType } from '../types';
import type { MapThemeColors } from '../utils/color-scale';
import { MAX_ZOOM, MIN_ZOOM } from '../utils/constants';
import type { ChoroplethLayerConfig } from './ChoroplethConfig';

export class LayerManager {
  private map: MapLibreMap | null = null;
  private highlightLayerIdsByConfigId: Map<string, string> = new Map();
  private colors: MapThemeColors;

  constructor(colors: MapThemeColors) {
    this.colors = colors;
  }

  init(map: MapLibreMap) {
    this.map = map;
  }

  setColors(colors: MapThemeColors) {
    this.colors = colors;
  }

  applyThemeColors(config: ChoroplethLayerConfig) {
    if (!this.map) return;
    if (this.map.getLayer(config.outlineLayerId)) {
      this.map.setPaintProperty(
        config.outlineLayerId,
        'line-color',
        this.colors.border
      );
    }
    const hlId = this.highlightLayerIdsByConfigId.get(config.id);
    if (hlId && this.map.getLayer(hlId)) {
      this.map.setPaintProperty(hlId, 'line-color', this.colors.highlight);
    }
  }

  addSource(config: ChoroplethLayerConfig) {
    if (!this.map) return;
    if (!this.map.getSource(config.sourceId)) {
      if (config.sourceType === 'vector') {
        const vectorSource: VectorSourceSpecification = {
          type: 'vector',
          url: config.sourceUrl,
          promoteId: config.promoteId ?? config.propertyName,
        };
        this.map.addSource(config.sourceId, vectorSource);
      } else if (config.sourceType === 'geojson' && config.sourceUrl) {
        this.map.addSource(config.sourceId, {
          type: 'geojson',
          data: config.sourceUrl,
          promoteId: config.promoteId ?? config.propertyName,
        });
      }
    }
  }

  // The choropleth sinks below the basemap's water fill, so OSM water
  // repaints the sea and clips every polygon to the real coastline —
  // otherwise the boundary geometry (Natural Earth countries especially)
  // bleeds past it.
  private insertionLayerId(labelLayerId: string): string {
    if (labelLayerId) return labelLayerId;
    return this.map?.getLayer(MAPLIBRE_WATER_LAYER_ID)
      ? MAPLIBRE_WATER_LAYER_ID
      : '';
  }

  // The country and region rings trace a coastline offset from the OSM one
  // underneath, doubling every shore. The basemap's own admin-0 and coastline
  // lines draw the same borders from the OSM geometry, so those outline
  // layers are left out and the basemap carries them.
  private skipsOutline(config: ChoroplethLayerConfig): boolean {
    return (
      config.id === LocationType.COUNTRY || config.id === LocationType.REGION
    );
  }

  private dataFilter(
    config: ChoroplethLayerConfig,
    keys: string[]
  ): FilterSpecification {
    const keyFilter: ExpressionSpecification = [
      'in',
      ['to-string', ['get', config.propertyName]],
      ['literal', keys],
    ];
    return keyFilter;
  }

  setDataFilter(config: ChoroplethLayerConfig, keys: string[]) {
    if (!this.map) return;
    const filter = this.dataFilter(config, keys);
    const layerIds = [
      config.fillLayerId,
      this.highlightLayerIdsByConfigId.get(config.id),
    ];
    for (const layerId of layerIds) {
      if (layerId && this.map.getLayer(layerId)) {
        this.map.setFilter(layerId, filter);
      }
    }
  }

  addLayers(config: ChoroplethLayerConfig, labelLayerId: string) {
    if (!this.map) return;

    const beforeId = this.insertionLayerId(labelLayerId);

    const baseLayerConfig: FillLayerSpecification = {
      id: config.fillLayerId,
      type: 'fill',
      source: config.sourceId,
      minzoom: config.minzoom,
      maxzoom: config.maxzoom,
      paint: {
        'fill-color': 'transparent',
        'fill-opacity': 0,
        'fill-color-transition': {
          duration: 0,
          delay: 0,
        },
        'fill-opacity-transition': {
          duration: 0,
          delay: 0,
        },
      },
      filter: this.dataFilter(config, []),
    };

    if (!this.map.getLayer(config.fillLayerId)) {
      const fillLayerConfig: FillLayerSpecification =
        config.sourceType === 'vector'
          ? { ...baseLayerConfig, 'source-layer': config.sourceLayer }
          : baseLayerConfig;

      if (beforeId) {
        this.map.addLayer(fillLayerConfig, beforeId);
      } else {
        this.map.addLayer(fillLayerConfig);
      }
    }

    if (!this.map.getLayer(config.outlineLayerId)) {
      const outlineLineWidth: ExpressionSpecification = [
        'interpolate',
        ['linear'],
        ['zoom'],
        MIN_ZOOM,
        0.75,
        MAX_ZOOM,
        2,
      ];

      const outlineLayerConfig: LineLayerSpecification = {
        id: config.outlineLayerId,
        type: 'line',
        source: config.sourceId,
        minzoom: config.minzoom,
        maxzoom: config.maxzoom,
        paint: {
          'line-width': outlineLineWidth,
          'line-color': this.colors.border,
          'line-opacity': 1,
        },
      };

      const finalOutlineConfig =
        config.sourceType === 'vector'
          ? { ...outlineLayerConfig, 'source-layer': config.sourceLayer }
          : outlineLayerConfig;

      if (!this.skipsOutline(config)) {
        if (beforeId) {
          this.map.addLayer(finalOutlineConfig, beforeId);
        } else {
          this.map.addLayer(finalOutlineConfig);
        }
      }

      // Dedicated highlight layer on top
      const highlightId = `${config.outlineLayerId}-hover-highlight`;
      if (!this.map.getLayer(highlightId)) {
        const highlightLineWidth: ExpressionSpecification = [
          'interpolate',
          ['linear'],
          ['zoom'],
          MIN_ZOOM,
          1,
          MAX_ZOOM,
          2,
        ];

        const highlightOpacity: ExpressionSpecification = [
          'case',
          ['boolean', ['feature-state', 'hover'], false],
          1,
          0,
        ];

        const base: LineLayerSpecification = {
          id: highlightId,
          type: 'line',
          source: config.sourceId,
          minzoom: config.minzoom,
          maxzoom: config.maxzoom,
          paint: {
            'line-color': this.colors.highlight,
            'line-width': highlightLineWidth,
            'line-opacity': highlightOpacity,
          },
          filter: this.dataFilter(config, []),
        };
        const finalHighlightConfig =
          config.sourceType === 'vector'
            ? { ...base, 'source-layer': config.sourceLayer }
            : base;
        this.map.addLayer(finalHighlightConfig);
        this.map.moveLayer(highlightId);
      }
      this.highlightLayerIdsByConfigId.set(config.id, highlightId);
    }
  }

  setPaint(
    config: ChoroplethLayerConfig,
    fillColorExpr: DataDrivenPropertyValueSpecification<string> | string
  ) {
    if (!this.map || !config) return;
    if (this.map.getLayer(config.fillLayerId)) {
      this.map.setPaintProperty(
        config.fillLayerId,
        'fill-color',
        fillColorExpr
      );
    }
  }

  setOpacity(
    config: ChoroplethLayerConfig,
    fillOpacity: number,
    lineOpacity: number
  ) {
    if (!this.map) return;

    if (this.map.getLayer(config.fillLayerId)) {
      this.map.setPaintProperty(
        config.fillLayerId,
        'fill-opacity',
        fillOpacity
      );
    }

    if (this.map.getLayer(config.outlineLayerId)) {
      this.map.setPaintProperty(
        config.outlineLayerId,
        'line-opacity',
        lineOpacity
      );
    }
  }

  setZoomRange(
    config: ChoroplethLayerConfig,
    minzoom: number,
    maxzoom: number
  ) {
    if (!this.map) return;
    if (this.map.getLayer(config.fillLayerId)) {
      this.map.setLayerZoomRange(config.fillLayerId, minzoom, maxzoom);
    }
    if (this.map.getLayer(config.outlineLayerId)) {
      this.map.setLayerZoomRange(config.outlineLayerId, minzoom, maxzoom);
    }
  }

  removeAll(configs: ChoroplethLayerConfig[]) {
    if (!this.map) return;
    configs.forEach((config) => {
      const hlId = this.highlightLayerIdsByConfigId.get(config.id);
      if (hlId && this.map?.getLayer(hlId)) {
        this.map.removeLayer(hlId);
      }
      if (this.map?.getLayer(config.fillLayerId)) {
        this.map.removeLayer(config.fillLayerId);
      }
      if (this.map?.getLayer(config.outlineLayerId)) {
        this.map.removeLayer(config.outlineLayerId);
      }
      if (this.map?.getSource(config.sourceId)) {
        this.map.removeSource(config.sourceId);
      }
    });
    this.highlightLayerIdsByConfigId.clear();
  }

  setHoverState(
    config: ChoroplethLayerConfig,
    featureId: string | number | undefined,
    isHovered: boolean
  ) {
    if (!this.map) return;
    if (featureId == null || featureId === '') return;
    const hlId = this.highlightLayerIdsByConfigId.get(config.id);
    if (!hlId || !this.map.getLayer(hlId)) return;

    const target: {
      source: string;
      sourceLayer?: string;
      id: string | number;
    } = {
      source: config.sourceId,
      id: featureId,
    };

    if (config.sourceType === 'vector' && config.sourceLayer) {
      target.sourceLayer = config.sourceLayer;
    }

    this.map.setFeatureState(target, { hover: isHovered });
  }
}
